import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, TrendingUp } from "lucide-react";
import { api, API } from "../lib/api";

/**
 * Public deep-link page for a shareable win card — the landing destination
 * for followers who click through a subscriber's shared PNG. Renders the
 * card, quick trade stats, methodology strip, and a signup CTA.
 *
 * Route: /win/:id
 */
export default function WinPage() {
  const { id } = useParams();
  const [win, setWin] = useState(null);
  const [error, setError] = useState(null);

  // ── OG tag injection (client-side; social crawlers still hit the API
  //    endpoint directly, but pasting the URL into Slack/Discord will
  //    render nice previews once the shared /api/win/{id} JSON is cached).
  useEffect(() => {
    if (!id) return;
    api.get(`/win/${id}`)
      .then((r) => {
        setWin(r.data);
        try {
          document.title = `${r.data.symbol} · ${r.data.return_pct >= 0 ? "+" : ""}${r.data.return_pct.toFixed(1)}% · moonbucks`;
          _setMeta("og:title", r.data.og?.title);
          _setMeta("og:description", r.data.og?.description);
          _setMeta("og:image", r.data.og?.image);
          _setMeta("og:url", r.data.og?.url);
          _setMeta("twitter:card", "summary_large_image");
          _setMeta("twitter:image", r.data.og?.image);
          _setMeta("description", r.data.og?.description, "name");
        } catch { /* ignore */ }
      })
      .catch((e) => setError(e?.response?.status === 404 ? "Win not found" : "Something went wrong"));
  }, [id]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "#030110" }}>
        <div className="max-w-md text-center">
          <div className="text-xs uppercase tracking-widest text-mb-violet font-bold">moonbucks</div>
          <h1 className="text-3xl font-bold mt-3" style={{ color: "var(--mb-ink)" }}>Not found</h1>
          <p className="mt-2" style={{ color: "var(--mb-ink-muted)" }}>{error}. This share link may have expired.</p>
          <Link to="/" className="inline-block mt-6 px-5 py-2.5 rounded-md text-sm font-bold" style={{ backgroundColor: "var(--mb-emerald)", color: "#030110" }}>
            See the live desk →
          </Link>
        </div>
      </div>
    );
  }

  if (!win) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#030110" }}>
        <div className="text-sm font-mono-data tracking-wider" style={{ color: "var(--mb-ink-muted)" }}>Loading…</div>
      </div>
    );
  }

  const cardUrl = `${API}/win/${id}/card.jpg`;
  const isMoon = win.alert_class === "moonshot";
  const days = win.days_held ?? "—";
  const ret = win.return_pct ?? 0;

  return (
    <div className="min-h-screen" style={{ background: "#030110", color: "var(--mb-ink)" }}>
      <div className="max-w-4xl mx-auto px-6 py-14">
        <Link to="/" className="text-xs uppercase tracking-widest font-bold" style={{ color: "var(--mb-violet)" }}>
          ← moonbucks
        </Link>

        <div className="mt-8 mb-4 text-[11px] uppercase tracking-widest font-bold" style={{ color: "var(--mb-ink-muted)" }}>
          Closed alert · trade #{id.slice(0, 8)}
        </div>
        <h1
          className="font-bold tracking-tight"
          style={{ fontSize: "clamp(2.25rem, 5vw, 3.5rem)", lineHeight: 1.05, color: "var(--mb-ink)" }}
          data-testid="win-headline"
        >
          {win.symbol} closed <span style={{ color: ret >= 0 ? "#6EE7A0" : "#FF5C5C" }}>{ret >= 0 ? "+" : ""}{ret.toFixed(1)}%</span> in {days} days.
        </h1>
        <p className="mt-4 max-w-2xl text-lg" style={{ color: "var(--mb-ink-muted)" }}>
          Vetted at entry by the moonbucks desk with a full {win.grade || "—"}-grade conviction stack.
          Held to {(win.close_reason || "target").replace(/_/g, " ")}. Not personalized advice — this is one
          trade from an evolving track record.
        </p>

        <div className="mt-10 rounded-xl overflow-hidden border" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
          <img src={cardUrl} alt={`${win.symbol} win card`} className="w-full h-auto block" data-testid="win-card-img" />
        </div>

        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatBox label="Return" value={`${ret >= 0 ? "+" : ""}${ret.toFixed(1)}%`} accent={ret >= 0 ? "#6EE7A0" : "#FF5C5C"} />
          <StatBox label="Days held" value={days} />
          <StatBox label="Class" value={isMoon ? "Moonshot" : "Steady"} />
          <StatBox label="Grade" value={win.grade || "—"} accent={win.grade === "S" ? "#FF4D9D" : (win.grade === "A" ? "#A855F7" : undefined)} />
        </div>

        {/* CTA */}
        <div
          className="mt-12 rounded-xl p-8 md:p-10 relative overflow-hidden"
          style={{
            background: "radial-gradient(circle at top right, rgba(168,85,247,0.35) 0%, rgba(3,1,16,0.9) 60%)",
            border: "1px solid rgba(168,85,247,0.3)",
          }}
          data-testid="win-cta"
        >
          <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color: "#F5C56A" }}>
            See what's on the desk right now
          </div>
          <div className="mt-3 text-2xl md:text-3xl font-bold" style={{ color: "var(--mb-ink)" }}>
            One vetted setup per class, per day.
          </div>
          <p className="mt-3 max-w-xl" style={{ color: "var(--mb-ink-muted)" }}>
            Moonbucks scans 300+ tickers overnight, filters through a 5-signal conviction stack, and only
            promotes what clears the desk. Full methodology + rolling track record — public.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-md font-bold text-sm"
              style={{ backgroundColor: "var(--mb-emerald)", color: "#030110" }}
              data-testid="win-cta-signup"
            >
              Start free trial <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/track-record"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm border"
              style={{ borderColor: "rgba(255,255,255,0.15)", color: "var(--mb-ink)" }}
              data-testid="win-cta-track-record"
            >
              <TrendingUp className="w-4 h-4" /> Full track record
            </Link>
          </div>
        </div>

        <p className="mt-8 text-xs" style={{ color: "var(--mb-ink-subtle)" }}>
          Past performance does not guarantee future results. Moonbucks is education / research — not
          a broker, and not personalized investment advice.
        </p>
      </div>
    </div>
  );
}

function StatBox({ label, value, accent }) {
  return (
    <div
      className="rounded-md p-4"
      style={{
        backgroundColor: "var(--mb-surface-elevated)",
        border: "1px solid var(--mb-border)",
      }}
    >
      <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "var(--mb-ink-muted)" }}>{label}</div>
      <div className="mt-1.5 font-mono-data font-bold text-xl" style={{ color: accent || "var(--mb-ink)" }}>{value}</div>
    </div>
  );
}

function _setMeta(prop, content, kind = "property") {
  if (!content) return;
  let el = document.head.querySelector(`meta[${kind}='${prop}']`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(kind, prop);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
